import { calendarDateType } from "../../ControlPanel/_shared";
import type { CountSpecMisc } from "../../Processing/types/builderMisc";
import type { StatComponentSpec, MetricOption, SubjectEntity } from "../../Processing/types";
import type { DateRangeFormFields } from "../shared/DateRangeForm";
import type { PlayersFormFields } from "../shared/PlayersForm";
import { DATE_RANGE_DEFAULTS } from "../shared/DateRangeForm";
import { PLAYERS_DEFAULTS, resolveSubjectNames } from "../shared/PlayersForm";

// ── State ──

export interface StatFormState extends DateRangeFormFields, PlayersFormFields {
  queryId: string;
  label: string;
  unit: string;
  selectedMetricId: string | null;
  /** Which items a count metric tallies. Absent for every measured metric. */
  countSpec?: CountSpecMisc;
  activeCategory: string;
  searchQuery: string;
  metricAgg: "avg" | "sum" | "best";
  comparisonType: string;
  expandedKey: string | string[];
  excludeInjuredPlayers: boolean;
  showTeamPosData: boolean;
}

// ── Actions ──

export type StatAction =
  | { type: "SET_LABEL"; label: string }
  | { type: "SET_UNIT"; unit: string }
  | { type: "SET_METRIC"; metricId: string; metricName?: string; prevMetricName?: string }
  | { type: "SET_ACTIVE_CATEGORY"; category: string }
  | { type: "SET_SEARCH_QUERY"; query: string }
  | { type: "SET_METRIC_AGG"; agg: "avg" | "sum" | "best" }
  | { type: "SET_COUNT_SPEC"; countSpec?: CountSpecMisc }
  | { type: "SET_COMPARISON_TYPE"; value: string }
  | { type: "SET_EXPANDED_KEY"; key: string | string[] }
  | { type: "SET_EXCLUDE_INJURED"; value: boolean }
  | { type: "SET_SHOW_TEAM_POS_DATA"; value: boolean }
  | { type: "UPDATE_DATE_RANGE"; patch: Partial<DateRangeFormFields> }
  | { type: "UPDATE_PLAYERS"; patch: Partial<PlayersFormFields> };

// ── Reducer ──

export function statReducer(state: StatFormState, action: StatAction): StatFormState {
  switch (action.type) {
    case "SET_LABEL": return { ...state, label: action.label };
    case "SET_UNIT": return { ...state, unit: action.unit };
    case "SET_METRIC": {
      const labelIsAutoFilled = !state.label || state.label === action.prevMetricName;
      return { ...state, selectedMetricId: action.metricId, label: labelIsAutoFilled ? (action.metricName ?? "") : state.label };
    }
    case "SET_COUNT_SPEC":
      return { ...state, countSpec: action.countSpec };
    case "SET_ACTIVE_CATEGORY": return { ...state, activeCategory: action.category };
    case "SET_SEARCH_QUERY": return { ...state, searchQuery: action.query };
    case "SET_METRIC_AGG": return { ...state, metricAgg: action.agg };
    case "SET_COMPARISON_TYPE": return { ...state, comparisonType: action.value };
    case "SET_EXPANDED_KEY": return { ...state, expandedKey: action.key };
    case "SET_EXCLUDE_INJURED": return { ...state, excludeInjuredPlayers: action.value };
    case "SET_SHOW_TEAM_POS_DATA": return { ...state, showTeamPosData: action.value };
    case "UPDATE_DATE_RANGE": return { ...state, ...action.patch };
    case "UPDATE_PLAYERS": return { ...state, ...action.patch };
    default: return state;
  }
}

// ── Factories ──

export function createInitialState(): StatFormState {
  return {
    queryId: crypto.randomUUID(),
    label: "",
    unit: "",
    selectedMetricId: null,
    countSpec: undefined,
    activeCategory: "GPS",
    searchQuery: "",
    metricAgg: "avg",
    comparisonType: "latest_vs_prev",
    expandedKey: ["__metric"],
    excludeInjuredPlayers: true,
    showTeamPosData: true,
    ...DATE_RANGE_DEFAULTS,
    ...PLAYERS_DEFAULTS,
  };
}

export function hydrateFromSpec(spec: StatComponentSpec): StatFormState {
  const misc = spec.stat_tile_builder_misc;
  return {
    queryId: spec.queryId ?? crypto.randomUUID(),
    label: spec.display?.title ?? "",
    unit: misc?.unit ?? "",
    selectedMetricId: misc?.selectedMetricId ?? null,
    countSpec: misc?.countSpec,
    activeCategory: "GPS",
    searchQuery: "",
    metricAgg: misc?.metricAgg ?? "avg",
    comparisonType: misc?.comparisonType ?? "latest_vs_prev",
    expandedKey: [],
    dateRange: misc?.dateRange ?? DATE_RANGE_DEFAULTS.dateRange,
    showAdvancedDate: misc?.showAdvancedDate ?? DATE_RANGE_DEFAULTS.showAdvancedDate,
    startDateType: misc?.startDateType ?? DATE_RANGE_DEFAULTS.startDateType,
    startDateAbsolute: misc?.startDateAbsolute ?? DATE_RANGE_DEFAULTS.startDateAbsolute,
    startDateOffset: misc?.startDateOffset ?? DATE_RANGE_DEFAULTS.startDateOffset,
    endDateType: misc?.endDateType ?? DATE_RANGE_DEFAULTS.endDateType,
    endDateAbsolute: misc?.endDateAbsolute ?? DATE_RANGE_DEFAULTS.endDateAbsolute,
    endDateOffset: misc?.endDateOffset ?? DATE_RANGE_DEFAULTS.endDateOffset,
    dayFilter: misc?.dayFilter ?? DATE_RANGE_DEFAULTS.dayFilter,
    dateControllerId: misc?.dateControllerId,
    subjectControllerId: misc?.subjectControllerId,
    subjectControllerMode: misc?.subjectControllerMode,
    selectedSubjects: misc?.selectedSubjects ?? PLAYERS_DEFAULTS.selectedSubjects,
    selectedDrills: misc?.selectedDrills ?? PLAYERS_DEFAULTS.selectedDrills,
    subjectActiveTab: misc?.subjectActiveTab ?? PLAYERS_DEFAULTS.subjectActiveTab,
    excludeInjuredPlayers: misc?.excludeInjuredPlayers ?? true,
    showTeamPosData: misc?.showTeamPosData ?? true,
  };
}

export function formStateToSpec(
  state: StatFormState,
  metrics: MetricOption[],
  getSubjectEntities?: (subjectType: string) => SubjectEntity[],
): StatComponentSpec {
  const metric = metrics.find((m) => m.id === state.selectedMetricId);
  const title = state.label || metric?.name || null;

  const selectedSubjectNames = getSubjectEntities
    ? resolveSubjectNames(state.selectedSubjects, state.selectedDrills, getSubjectEntities)
    : [];

  return {
    type: "stat_tile",
    display: { title },
    queryId: state.queryId,
    stat_tile_builder_misc: {
      selectedMetricId: state.selectedMetricId,
      countSpec: state.countSpec,
      metricAgg: state.metricAgg,
      comparisonType: state.comparisonType,
      unit: state.unit || undefined,
      dateRange: state.dateRange,
      showAdvancedDate: state.showAdvancedDate,
      startDateType: calendarDateType(state.startDateType),
      startDateAbsolute: state.startDateAbsolute,
      startDateOffset: state.startDateOffset,
      endDateType: state.endDateType,
      endDateAbsolute: state.endDateAbsolute,
      endDateOffset: state.endDateOffset,
      dayFilter: state.dayFilter,
      dateControllerId: state.dateControllerId,
      subjectControllerId: state.subjectControllerId,
      subjectControllerMode: state.subjectControllerMode,
      selectedSubjects: state.selectedSubjects,
      selectedDrills: state.selectedDrills,
      subjectActiveTab: state.subjectActiveTab,
      selectedSubjectNames,
      excludeInjuredPlayers: state.excludeInjuredPlayers === false ? false : undefined,
      showTeamPosData: state.showTeamPosData === false ? false : undefined,
    },
  };
}
